import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { FORM_ENDPOINT, FORM_EMAIL_TO } from '../config.js';
import trainerImg from '../assets/story.jpeg';

const questions = [
  {
    id: 'situatie',
    label: 'Care e situația ta de acum?',
    type: 'single',
    options: [
      'M-am îngrășat în ultimul an și vreau să schimb asta',
      'Am succes profesional, dar corpul arată fix opusul',
      'Sunt slab/ă cu burtă și fără tonus muscular (skinny fat)',
      'Am burtă de mai mult de 1 an și mă simt la limită cu energia',
      'Trebuie să dau jos 15-30 kg urgent'
    ]
  },
  {
    id: 'obiectiv_6luni',
    label: 'Ce îți dorești să obții în 6 luni de acum?',
    type: 'single',
    options: [
      'Să dau burta jos și să mă simt energic/ă',
      'Să port haine cu 1-2 numere mai mici',
      'Să am energia și pofta de viață de acum 5-10 ani',
      'Să construiesc obiceiuri pe termen lung',
      'Toate de mai sus'
    ]
  },
  {
    id: 'experienta',
    label: 'Ce ai mai încercat până acum?',
    subtitle: 'Poți alege mai multe variante.',
    type: 'multi',
    options: [
      'Sală pe cont propriu / YouTube',
      'Program / PDF cumpărat',
      'Nutriționist sau Antrenor',
      'Aplicații de numărat calorii',
      'Post Intermitent sau alte diete stricte',
      'Suplimente / Detox / Shake-uri etc.',
      'Nu am încercat nimic serios'
    ]
  },
  {
    id: 'problema_nr1',
    label: 'Care simți că e problema #1 în acest moment?',
    type: 'text',
    placeholder: 'Scrie răspunsul tău aici...'
  },
  {
    id: 'name',
    label: 'Cum te cheamă?',
    type: 'text',
    placeholder: 'Numele tău'
  },
  {
    id: 'ocupatie',
    label: 'Cu ce te ocupi?',
    subtitle: 'Mă interesează în special dacă e un job sedentar sau nu.',
    type: 'text',
    placeholder: 'Scrie răspunsul tău aici...'
  },
  {
    id: 'phone',
    label: 'Nr. Telefon',
    subtitle: 'Dacă nu ești din România, adaugă prefixul țării.',
    type: 'tel',
    placeholder: '07xx xxx xxx'
  },
  {
    id: 'email',
    label: 'E-mail',
    type: 'email',
    placeholder: 'name@example.com'
  },
  {
    id: 'instagram',
    label: 'Cum te găsesc pe Instagram?',
    type: 'text',
    placeholder: '@username'
  },
  {
    id: 'urgenta',
    label: 'Cât de repede îți dorești să începi?',
    type: 'single',
    options: [
      'Cât de repede posibil',
      'În 7-14 zile',
      'În 30 de zile',
      'Nu vreau să încep acum, doar caut informații'
    ]
  },
  {
    id: 'buget',
    label: 'Dacă ai ști că această colaborare îți garantează rezultatele dorite, cât ai fi dispus/ă să investești pe lună în sănătatea ta?',
    type: 'single',
    options: [
      'sub 100 EUR',
      '150 - 300 EUR',
      '300 - 500 EUR'
    ]
  }
];

const letterKeys = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const slideVariants = {
  enter: { opacity: 0, y: 40 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -40 }
};

const QuizForm = () => {
  const [step, setStep] = useState(-1); // -1 = intro, questions.length = thank you
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);

  const currentQ = step >= 0 && step < questions.length ? questions[step] : null;

  const goNext = useCallback(() => {
    if (step < questions.length) {
      setStep(prev => prev + 1);
    }
  }, [step]);

  const goPrev = useCallback(() => {
    if (step > -1) {
      setStep(prev => prev - 1);
    }
  }, [step]);

  // Focus text input when step changes
  useEffect(() => {
    if (currentQ && ['text', 'email', 'tel'].includes(currentQ.type)) {
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [step, currentQ]);

  // Keyboard handler for Enter
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (step === -1) {
          goNext();
          return;
        }
        if (currentQ && ['text', 'email', 'tel'].includes(currentQ.type)) {
          const val = answers[currentQ.id];
          if (val && String(val).trim()) {
            goNext();
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, currentQ, answers, goNext]);

  const handleSingleSelect = (option) => {
    setAnswers(prev => ({ ...prev, [currentQ.id]: option }));
    // Auto-advance after short delay
    setTimeout(() => goNext(), 350);
  };

  const handleMultiSelect = (option) => {
    setAnswers(prev => {
      const current = prev[currentQ.id] || [];
      const updated = current.includes(option)
        ? current.filter(o => o !== option)
        : [...current, option];
      return { ...prev, [currentQ.id]: updated };
    });
  };

  const handleTextChange = (value) => {
    setAnswers(prev => ({ ...prev, [currentQ.id]: value }));
  };

  const canAdvance = () => {
    if (!currentQ) return false;
    const val = answers[currentQ.id];
    if (currentQ.type === 'single') return Boolean(val);
    if (currentQ.type === 'multi') return Array.isArray(val) && val.length > 0;
    return val && String(val).trim().length > 0;
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    // Build form data
    const fd = new FormData();
    questions.forEach(q => {
      const val = answers[q.id];
      if (val) {
        fd.append(q.id, Array.isArray(val) ? val.join(', ') : val);
      }
    });

    if (!FORM_ENDPOINT) {
      // Fallback: mailto
      const subject = encodeURIComponent('Aplicație nouă - ' + (answers.name || 'Necunoscut'));
      const bodyParts = questions.map(q => {
        const val = answers[q.id];
        const display = Array.isArray(val) ? val.join(', ') : (val || '-');
        return `${q.label}\n${display}`;
      });
      const body = encodeURIComponent(bodyParts.join('\n\n'));
      window.location.href = `mailto:${FORM_EMAIL_TO}?subject=${subject}&body=${body}`;
      setSubmitted(true);
      setSubmitting(false);
      setStep(questions.length);
      return;
    }

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd
      });
      if (!res.ok) throw new Error('Failed');
      setSubmitted(true);
      setStep(questions.length);
    } catch {
      // Still show thank you but note the error
      setSubmitted(true);
      setStep(questions.length);
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-submit when reaching the end
  useEffect(() => {
    if (step === questions.length && !submitted && !submitting) {
      handleSubmit();
    }
  }, [step, submitted, submitting]);

  const progress = step >= 0 ? ((step + 1) / (questions.length + 1)) * 100 : 0;

  return (
    <div className="min-h-screen bg-dark-950 text-white flex flex-col">
      {/* Progress bar */}
      {step >= 0 && step < questions.length && (
        <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-dark-800">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      )}

      {/* Back button */}
      {step > 0 && step < questions.length && (
        <button
          onClick={goPrev}
          className="fixed top-6 left-6 z-50 p-3 rounded-xl bg-dark-800/80 backdrop-blur-sm text-dark-400 hover:text-white hover:bg-dark-700 transition-colors"
          aria-label="Înapoi"
        >
          <FaArrowLeft size={16} />
        </button>
      )}

      {/* Step counter */}
      {step >= 0 && step < questions.length && (
        <div className="fixed top-6 right-6 z-50 text-xs text-dark-500 font-medium">
          {step + 1} / {questions.length}
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* INTRO SCREEN */}
        {step === -1 && (
          <motion.div
            key="intro"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4 }}
            className="flex-1 flex flex-col lg:flex-row min-h-screen"
          >
            {/* Image */}
            <div className="w-full lg:w-1/2 h-[45vh] lg:h-screen relative">
              <img
                src={trainerImg}
                alt="Catalin Dorca"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark-950/50 lg:bg-gradient-to-r lg:from-transparent lg:to-dark-950" />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-transparent to-transparent lg:hidden" />
            </div>

            {/* Content */}
            <div className="flex-1 flex items-center justify-center px-6 sm:px-10 py-10 lg:py-0">
              <div className="max-w-lg">
                <p className="text-lg sm:text-xl text-dark-300 leading-relaxed mb-6">
                  Dacă ai ajuns aici înseamnă că ești gata să scapi de burtă și să ai mai multă energie,
                </p>
                <p className="text-lg sm:text-xl text-white font-semibold leading-relaxed mb-6">
                  Fără să fii îți dai viața peste cap cu planuri stricte și antrenamente de 2 ore.
                </p>
                <p className="text-lg sm:text-xl text-white font-semibold leading-relaxed mb-4">
                  Răspunde la întrebări, durează 2 minute, și programează apelul de cunoaștere cu mine.
                </p>
                <p className="text-sm text-dark-500 italic mb-8">
                  Nu completa dacă ești în căutare doar de sfaturi gratuite.
                </p>
                <button
                  onClick={goNext}
                  className="px-10 py-4 text-base font-semibold rounded-xl bg-dark-100 text-dark-900 hover:bg-white transition-colors"
                >
                  Start
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* QUESTION SCREENS */}
        {currentQ && (
          <motion.div
            key={`q-${step}`}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35 }}
            className="flex-1 flex items-center justify-center min-h-screen px-6 sm:px-10"
          >
            <div className="w-full max-w-2xl">
              {/* Question number + label */}
              <div className="flex items-start gap-3 mb-2">
                <span className="flex-shrink-0 mt-1 text-xs font-bold text-dark-500 bg-dark-800 px-2 py-0.5 rounded">
                  {step + 1}
                </span>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold leading-snug">
                  {currentQ.label}
                </h2>
              </div>

              {currentQ.subtitle && (
                <p className="text-sm text-dark-400 ml-9 mb-6">{currentQ.subtitle}</p>
              )}
              {!currentQ.subtitle && <div className="mb-6" />}

              {/* SINGLE SELECT */}
              {currentQ.type === 'single' && (
                <div className="space-y-2.5 ml-9">
                  {currentQ.options.map((opt, i) => {
                    const selected = answers[currentQ.id] === opt;
                    return (
                      <button
                        key={i}
                        onClick={() => handleSingleSelect(opt)}
                        className={`w-full flex items-center gap-3 px-5 py-3.5 rounded-xl text-left text-sm sm:text-base transition-all duration-200 border ${
                          selected
                            ? 'border-primary bg-primary/10 text-white'
                            : 'border-dark-700 bg-dark-900 text-dark-300 hover:border-dark-500 hover:bg-dark-800'
                        }`}
                      >
                        <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-xs font-bold ${
                          selected ? 'bg-primary text-white' : 'bg-dark-800 text-dark-400'
                        }`}>
                          {letterKeys[i]}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* MULTI SELECT */}
              {currentQ.type === 'multi' && (
                <>
                  <div className="space-y-2.5 ml-9">
                    {currentQ.options.map((opt, i) => {
                      const selected = (answers[currentQ.id] || []).includes(opt);
                      return (
                        <button
                          key={i}
                          onClick={() => handleMultiSelect(opt)}
                          className={`w-full flex items-center gap-3 px-5 py-3.5 rounded-xl text-left text-sm sm:text-base transition-all duration-200 border ${
                            selected
                              ? 'border-primary bg-primary/10 text-white'
                              : 'border-dark-700 bg-dark-900 text-dark-300 hover:border-dark-500 hover:bg-dark-800'
                          }`}
                        >
                          <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-xs font-bold ${
                            selected ? 'bg-primary text-white' : 'bg-dark-800 text-dark-400'
                          }`}>
                            {letterKeys[i]}
                          </span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {canAdvance() && (
                    <div className="ml-9 mt-6">
                      <button
                        onClick={goNext}
                        className="px-8 py-3 text-sm font-semibold rounded-xl bg-dark-100 text-dark-900 hover:bg-white transition-colors"
                      >
                        OK
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* TEXT / EMAIL / TEL */}
              {['text', 'email', 'tel'].includes(currentQ.type) && (
                <div className="ml-9">
                  <input
                    ref={inputRef}
                    type={currentQ.type}
                    value={answers[currentQ.id] || ''}
                    onChange={(e) => handleTextChange(e.target.value)}
                    placeholder={currentQ.placeholder}
                    className="w-full bg-transparent border-b-2 border-dark-600 focus:border-primary text-white text-lg sm:text-xl py-3 outline-none transition-colors placeholder:text-dark-600"
                  />
                  {canAdvance() && (
                    <div className="mt-6">
                      <button
                        onClick={goNext}
                        className="inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold rounded-xl bg-dark-100 text-dark-900 hover:bg-white transition-colors"
                      >
                        OK
                        <FaArrowRight size={12} />
                      </button>
                      <span className="text-xs text-dark-500 ml-3">sau apasă Enter</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* THANK YOU SCREEN */}
        {step === questions.length && (
          <motion.div
            key="done"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4 }}
            className="flex-1 flex flex-col lg:flex-row min-h-screen"
          >
            {/* Image */}
            <div className="w-full lg:w-1/2 h-[45vh] lg:h-screen relative">
              <img
                src={trainerImg}
                alt="Catalin Dorca"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark-950/50 lg:bg-gradient-to-r lg:from-transparent lg:to-dark-950" />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-transparent to-transparent lg:hidden" />
            </div>

            {/* Content */}
            <div className="flex-1 flex items-center justify-center px-6 sm:px-10 py-10 lg:py-0">
              <div className="max-w-lg">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 mb-6">
                  <FaCheckCircle className="text-primary" size={28} />
                </div>
                <p className="text-lg sm:text-xl text-dark-300 leading-relaxed mb-6">
                  Mă bucur mult că ai ajuns până aici!
                </p>
                <p className="text-lg sm:text-xl text-white leading-relaxed mb-6">
                  <strong>Următorul pas:</strong> te voi contacta în cel mai scurt timp pentru a stabili un apel de cunoaștere.
                </p>
                <p className="text-dark-400 mb-8">
                  Abia aștept să ne vedem!
                  <br />
                  <span className="text-white font-semibold mt-2 inline-block">Cătălin</span>
                </p>
                <a
                  href="/"
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold rounded-xl bg-primary text-white hover:bg-primary-700 transition-colors"
                >
                  Înapoi pe site
                  <FaArrowRight size={14} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuizForm;
